import React, { useState, useEffect } from 'react';
import { PortfolioService } from '../../services/PortfolioService';
import type { PortfolioWithQuotes } from '../../services/PortfolioService';
import { SearchBar } from '../ui/SearchBar';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { ScrollableTabBar } from '../ui/ScrollableTabBar';
import { PortfolioTable } from './PortfolioTable';
import { useToast } from '../../hooks/useToast';
import { PlusCircle, Plus, X, Briefcase, TrendingUp, TrendingDown } from 'lucide-react';
import type { SearchResult } from '../../types/market';
import type { PortfolioHolding } from '../../types/database';

interface PortfolioManagerEnhancedProps {
  userId: string;
  className?: string;
}

interface AddHoldingForm {
  ticker: string;
  companyName: string;
  shares: number;
  purchasePrice: number;
  purchaseDate: string;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5625rem 0.75rem',
  background: '#1a2235',
  border: '1px solid #1e2d45',
  borderRadius: 8,
  color: '#f1f5f9',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.18s, box-shadow 0.18s',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#475569',
  letterSpacing: '0.03em',
  marginBottom: '0.3rem',
  display: 'block',
};

export const PortfolioManagerEnhanced: React.FC<PortfolioManagerEnhancedProps> = ({
  userId,
  className = '',
}) => {
  const [portfolios, setPortfolios] = useState<PortfolioWithQuotes[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showAddHolding, setShowAddHolding] = useState(false);
  const [nameInputFocused, setNameInputFocused] = useState(false);
  const [holdingForm, setHoldingForm] = useState<AddHoldingForm>({
    ticker: '', companyName: '', shares: 0, purchasePrice: 0,
    purchaseDate: new Date().toISOString().split('T')[0],
  });
  const { addToast } = useToast();

  useEffect(() => { loadPortfolios(); }, [userId]);
  useEffect(() => {
    const unsub = PortfolioService.subscribeToPortfolioChanges(userId, loadPortfolios);
    return unsub;
  }, [userId]);

  const loadPortfolios = async () => {
    try {
      setIsLoading(true);
      const data = await PortfolioService.getUserPortfolios(userId, true);
      setPortfolios(data);
      if (data.length > 0 && !activeId) setActiveId(data[0].id);
    } catch {
      addToast('Failed to load portfolios', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) { addToast('Enter a portfolio name', 'warning'); return; }
    try {
      setIsCreating(true);
      const created = await PortfolioService.createPortfolio({ user_id: userId, name: newName.trim(), holdings: [] });
      const pw: PortfolioWithQuotes = { ...created, holdings: [], totalValue: 0, totalCost: 0, totalGainLoss: 0, totalGainLossPercent: 0, dayChange: 0, dayChangePercent: 0 };
      setPortfolios(p => [pw, ...p]);
      setNewName('');
      setShowCreate(false);
      setActiveId(created.id);
      addToast(`"${created.name}" created`, 'success');
    } catch {
      addToast('Failed to create portfolio', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete "${name}"?`)) return;
    try {
      await PortfolioService.deletePortfolio(id);
      setPortfolios(p => p.filter(x => x.id !== id));
      if (activeId === id) {
        const rest = portfolios.filter(x => x.id !== id);
        setActiveId(rest.length > 0 ? rest[0].id : null);
      }
      addToast(`"${name}" deleted`, 'success');
    } catch {
      addToast('Failed to delete portfolio', 'error');
    }
  };

  const handleAddHolding = async () => {
    if (!activeId) { addToast('Select a portfolio first', 'warning'); return; }
    if (!holdingForm.ticker || holdingForm.shares <= 0 || holdingForm.purchasePrice <= 0) {
      addToast('Fill in all fields with valid values', 'warning'); return;
    }
    try {
      const h: PortfolioHolding = {
        ticker: holdingForm.ticker.toUpperCase(),
        name: holdingForm.companyName,
        shares: holdingForm.shares,
        purchase_price: holdingForm.purchasePrice,
        purchase_date: holdingForm.purchaseDate,
      };
      await PortfolioService.addHolding(activeId, h);
      await loadPortfolios();
      setHoldingForm({ ticker: '', companyName: '', shares: 0, purchasePrice: 0, purchaseDate: new Date().toISOString().split('T')[0] });
      setShowAddHolding(false);
      addToast(`${h.ticker} added to portfolio`, 'success');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to add holding', 'error');
    }
  };

  const handleRemoveHolding = async (ticker: string) => {
    if (!activeId) return;
    if (!confirm(`Remove ${ticker} from portfolio?`)) return;
    try {
      await PortfolioService.removeHolding(activeId, ticker);
      await loadPortfolios();
      addToast(`${ticker} removed`, 'success');
    } catch {
      addToast('Failed to remove holding', 'error');
    }
  };

  const active = portfolios.find(p => p.id === activeId);

  const fmt$0 = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
  const fmtPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;

  /* ── Loading ── */
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2.5rem' }}>
        <LoadingSpinner size="md" />
      </div>
    );
  }

  /* ── Empty state ── */
  if (portfolios.length === 0 && !showCreate) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <div style={{
          width: 52, height: 52, borderRadius: '50%',
          background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 1.125rem',
        }}>
          <Briefcase size={22} color="#a78bfa" strokeWidth={1.75} />
        </div>
        <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#cbd5e1', marginBottom: '0.375rem' }}>
          No portfolios yet
        </h3>
        <p style={{ fontSize: '0.8125rem', color: '#334155', marginBottom: '1.25rem' }}>
          Create a portfolio to start tracking your investments
        </p>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: '#8b5cf6', color: '#fff',
            padding: '0.5625rem 1.125rem', borderRadius: 9,
            fontSize: '0.8125rem', fontWeight: 600, border: 'none', cursor: 'pointer',
            transition: 'background 0.18s, box-shadow 0.18s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#7c3aed'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(139,92,246,0.3)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#8b5cf6'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <PlusCircle size={14} strokeWidth={2} /> Create Portfolio
        </button>
      </div>
    );
  }

  const isGain = (active?.totalGainLoss ?? 0) >= 0;

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <ScrollableTabBar
            tabs={portfolios.map(p => ({ id: p.id, label: p.name, count: p.holdings.length, isActive: p.id === activeId }))}
            activeTabId={activeId}
            onTabClick={setActiveId}
            onDeleteTab={handleDelete}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap' }}>
          {active && !showAddHolding && (
            <button
              onClick={() => setShowAddHolding(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '0.4375rem 0.875rem', borderRadius: 8,
                background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)',
                color: '#10b981', fontSize: '0.8125rem', fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.18s', whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(16,185,129,0.2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(16,185,129,0.12)'; }}
            >
              <Plus size={13} strokeWidth={2.5} /> Add Holding
            </button>
          )}
          <button
            onClick={() => setShowCreate(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '0.4375rem 0.875rem', borderRadius: 8,
              background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.25)',
              color: '#a78bfa', fontSize: '0.8125rem', fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.18s', whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.12)'; }}
          >
            <PlusCircle size={13} strokeWidth={2} /> New Portfolio
          </button>
        </div>
      </div>

      {/* ── Create portfolio form ── */}
      {showCreate && (
        <div style={{
          background: '#1a2235', border: '1px solid #1e2d45',
          borderRadius: 10, padding: '0.875rem',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          animation: 'slideUp 0.18s ease-out',
        }}>
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Portfolio name…"
            autoFocus
            style={{
              ...inputStyle,
              borderColor: nameInputFocused ? '#8b5cf6' : '#1e2d45',
              boxShadow: nameInputFocused ? '0 0 0 2px rgba(139,92,246,0.15)' : 'none',
            }}
            onFocus={() => setNameInputFocused(true)}
            onBlur={() => setNameInputFocused(false)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setShowCreate(false); setNewName(''); } }}
          />
          <button
            onClick={handleCreate}
            disabled={isCreating || !newName.trim()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0.5rem 0.875rem', borderRadius: 8, background: '#8b5cf6', color: '#fff', fontSize: '0.8125rem', fontWeight: 600, border: 'none', cursor: isCreating || !newName.trim() ? 'not-allowed' : 'pointer', opacity: isCreating || !newName.trim() ? 0.5 : 1, flexShrink: 0, transition: 'background 0.18s' }}
            onMouseEnter={e => { if (!isCreating && newName.trim()) e.currentTarget.style.background = '#7c3aed'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#8b5cf6'; }}
          >
            {isCreating ? <LoadingSpinner size="sm" /> : 'Create'}
          </button>
          <button
            onClick={() => { setShowCreate(false); setNewName(''); }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, background: 'transparent', border: '1px solid #1e2d45', cursor: 'pointer', color: '#475569', flexShrink: 0, transition: 'all 0.18s' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#f87171'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.25)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#475569'; e.currentTarget.style.borderColor = '#1e2d45'; }}
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      )}

      {/* ── Add holding form ── */}
      {active && showAddHolding && (
        <div style={{ background: '#1a2235', border: '1px solid #1e2d45', borderRadius: 12, overflow: 'hidden', animation: 'slideUp 0.18s ease-out' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderBottom: '1px solid #1e2d45', background: '#141e2e' }}>
            <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '-0.01em' }}>Add Holding</span>
            <button
              onClick={() => { setShowAddHolding(false); setHoldingForm({ ticker: '', companyName: '', shares: 0, purchasePrice: 0, purchaseDate: new Date().toISOString().split('T')[0] }); }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, background: 'transparent', border: 'none', cursor: 'pointer', color: '#334155', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#f87171'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#334155'; }}
            >
              <X size={14} strokeWidth={2} />
            </button>
          </div>
          <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.875rem' }}>
            {/* Stock search */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Stock Symbol</label>
              <SearchBar
                onSelect={r => setHoldingForm(p => ({ ...p, ticker: r.symbol, companyName: r.name }))}
                placeholder="Search for stock…"
                className="w-full"
              />
              {holdingForm.ticker && (
                <div style={{ marginTop: '0.375rem', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 6, padding: '0.2rem 0.5rem' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#60a5fa', fontFamily: "'JetBrains Mono', monospace" }}>{holdingForm.ticker}</span>
                  {holdingForm.companyName && <span style={{ fontSize: '0.72rem', color: '#475569' }}>{holdingForm.companyName}</span>}
                </div>
              )}
            </div>

            {/* Shares */}
            <div>
              <label style={labelStyle}>Shares</label>
              <input
                type="number"
                value={holdingForm.shares || ''}
                onChange={e => setHoldingForm(p => ({ ...p, shares: parseFloat(e.target.value) || 0 }))}
                min="0" step="0.01"
                placeholder="0.00"
                style={inputStyle}
                onFocus={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59,130,246,0.15)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = '#1e2d45'; e.currentTarget.style.boxShadow = 'none'; }}
              />
            </div>

            {/* Purchase price */}
            <div>
              <label style={labelStyle}>Purchase Price ($)</label>
              <input
                type="number"
                value={holdingForm.purchasePrice || ''}
                onChange={e => setHoldingForm(p => ({ ...p, purchasePrice: parseFloat(e.target.value) || 0 }))}
                min="0" step="0.01"
                placeholder="0.00"
                style={inputStyle}
                onFocus={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59,130,246,0.15)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = '#1e2d45'; e.currentTarget.style.boxShadow = 'none'; }}
              />
            </div>

            {/* Date */}
            <div>
              <label style={labelStyle}>Purchase Date</label>
              <input
                type="date"
                value={holdingForm.purchaseDate}
                onChange={e => setHoldingForm(p => ({ ...p, purchaseDate: e.target.value }))}
                style={{ ...inputStyle, colorScheme: 'dark' }}
                onFocus={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59,130,246,0.15)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = '#1e2d45'; e.currentTarget.style.boxShadow = 'none'; }}
              />
            </div>
          </div>

          {/* Form actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', borderTop: '1px solid #1e2d45' }}>
            <button
              onClick={handleAddHolding}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '0.5625rem 1.125rem', borderRadius: 8,
                background: '#10b981', color: '#fff',
                fontSize: '0.8125rem', fontWeight: 600, border: 'none', cursor: 'pointer',
                transition: 'background 0.18s, box-shadow 0.18s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#059669'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(16,185,129,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#10b981'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <Plus size={14} strokeWidth={2.5} /> Add Holding
            </button>
            <button
              onClick={() => { setShowAddHolding(false); setHoldingForm({ ticker: '', companyName: '', shares: 0, purchasePrice: 0, purchaseDate: new Date().toISOString().split('T')[0] }); }}
              style={{ padding: '0.5625rem 0.875rem', borderRadius: 8, background: 'transparent', border: '1px solid #1e2d45', color: '#64748b', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.18s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e2d45'; e.currentTarget.style.color = '#64748b'; }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Portfolio panel ── */}
      {active && (
        <div style={{ background: '#0f1621', border: '1px solid #1a2235', borderRadius: 12, overflow: 'hidden' }}>
          {/* Summary bar */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '1px',
            background: '#1a2235',
            borderBottom: '1px solid #1a2235',
          }}>
            {[
              { label: 'Holdings',     value: String(active.holdings.length),               color: '#94a3b8', mono: false },
              { label: 'Total Value',  value: fmt$0(active.totalValue),                      color: '#f1f5f9', mono: true  },
              { label: 'Invested',     value: fmt$0(active.totalCost),                       color: '#64748b', mono: true  },
              { label: 'Total G/L',    value: `${active.totalGainLoss >= 0 ? '+' : ''}${fmt$0(active.totalGainLoss)} (${fmtPct(active.totalGainLossPercent)})`, color: isGain ? '#10b981' : '#ef4444', mono: true },
            ].map((stat, i) => (
              <div key={i} style={{ background: '#0c111d', padding: '0.625rem 1rem', display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#1e3a5f' }}>
                  {stat.label}
                </span>
                <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: stat.color, fontFamily: stat.mono ? "'JetBrains Mono', monospace" : 'inherit', letterSpacing: stat.mono ? '-0.02em' : 'normal' }}>
                  {stat.value}
                </span>
              </div>
            ))}
          </div>

          {/* Day change strip */}
          {(active.dayChange !== 0 || active.holdings.length > 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.5rem 1rem', background: '#0a0e1a', borderBottom: '1px solid #1a2235' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Today</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {active.dayChange >= 0
                  ? <TrendingUp size={12} color="#10b981" strokeWidth={2.5} />
                  : <TrendingDown size={12} color="#ef4444" strokeWidth={2.5} />
                }
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: active.dayChange >= 0 ? '#10b981' : '#ef4444', fontFamily: "'JetBrains Mono', monospace" }}>
                  {active.dayChange >= 0 ? '+' : ''}{fmt$0(active.dayChange)} ({fmtPct(active.dayChangePercent)})
                </span>
              </div>
            </div>
          )}

          {/* Table */}
          <PortfolioTable holdings={active.holdings} onRemove={handleRemoveHolding} />
        </div>
      )}
    </div>
  );
};
